import React from "react";
import styled from "styled-components";
import tw from "twin.macro";
import Slider from "react-slick";
import { useTranslation } from "react-i18next";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const Container = tw.div`relative`;
const SingleColumn = tw.div`max-w-screen-xl mx-auto py-20 lg:py-24`;
const Content = tw.div`mt-16`;

const Card = styled.div(props => [
  tw`mt-24 md:flex justify-center items-center`,
  props.reversed ? tw`flex-row-reverse` : "flex-row"
]);

const ImageContainer = styled.div`
  ${tw`rounded md:w-1/2 lg:w-5/12 xl:w-1/3 flex-shrink-0 h-80 md:h-144 mx-4 sm:mx-8 md:mx-4 lg:mx-8`}
  overflow: hidden;

  @media (max-width: 768px) {
    ${tw`h-auto`}
  }
`;

const Image = styled.img`
  ${tw`w-full h-full object-cover`}

  @media (max-width: 768px) {
    ${tw`object-contain`}
  }
`;

const Video = styled.video`
  ${tw`w-full h-full object-cover`}
`;

const Details = tw.div`mt-4 md:mt-0 md:max-w-md mx-4 sm:mx-8 md:mx-4 lg:mx-8`;
const Subtitle = tw.div`font-bold tracking-wide text-gray-300`;
const Title = tw.h4`text-3xl font-bold text-white`;
const Description = tw.p`mt-2 text-sm leading-loose text-gray-300`;

const SlickSlider = styled(Slider)`
  .slick-list {
    ${tw`rounded`}
  }
  .slick-dots {
    ${tw`mt-2`}
  }
  .slick-dots li button:before {
    ${tw`text-gray-400`}
  }
  .slick-dots li.slick-active button:before {
    ${tw`text-white`}
  }
  .slick-prev, .slick-next {
    ${tw`text-gray-400 hover:text-white`}
  }
`;

const LabelPanel = styled.div`
  ${tw`w-full max-w-md mx-auto p-6 mb-12 rounded-md text-center`}
  background: linear-gradient(135deg, #2e2e2e, #1a1a1a);
  border: 2px solid #444;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.4);
  font-family: 'Orbitron', sans-serif;
  letter-spacing: 0.1em;
  color: #ccc;
  text-transform: uppercase;
  position: relative;

  &::before {
    content: 'MODULE 01';
    position: absolute;
    top: -1.5rem;
    left: 1rem;
    font-size: 0.75rem;
    color: #999;
    background: #111;
    padding: 0.2rem 0.5rem;
    border: 1px solid #333;
    border-radius: 4px;
  }
`;

const SmallHeading = styled.h2`
  ${tw`text-sm font-bold mb-2`}
  color: #eee;
`;

const SmallDescription = styled.p`
  ${tw`text-xs`}
  color: #aaa;
`;

export default () => {
  const { t } = useTranslation();

  const cards = t("visual.cards", { returnObjects: true });

  const settingsWithDots = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
  };

  const settingsWithoutDots = {
    ...settingsWithDots,
    dots: false,
  };

  return (
    <Container>
      <SingleColumn>
        <LabelPanel>
          <SmallHeading>{t("visual.title")}</SmallHeading>
          <SmallDescription>{t("visual.description")}</SmallDescription>
        </LabelPanel>

        <Content>
          {cards.map((card, i) => (
            <Card key={i} reversed={i % 2 === 1}>
              <ImageContainer>
                <SlickSlider {...(i === 0 ? settingsWithoutDots : settingsWithDots)}>
                  {card.images.map((media, index) => (
                    <div key={index}>
                      {media.endsWith(".mp4") ? (
                        <Video controls>
                          <source src={media} type="video/mp4" />
                        </Video>
                      ) : (
                        <Image src={media} alt={`Image ${index}`} />
                      )}
                    </div>
                  ))}
                </SlickSlider>
              </ImageContainer>
              <Details>
                <Subtitle>{card.subtitle}</Subtitle>
                <Title>{card.title}</Title>
                <Description>{card.description}</Description>
              </Details>
            </Card>
          ))}
        </Content>
      </SingleColumn>
    </Container>
  );
};
